import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { Mic, Check } from 'lucide-react';
import { useCopy } from './copy/copyMode';
type Mode = 'standard' | 'screenshot' | 'ask';
// Phases of the self-playing Spotlight demo:
//  idle     → backdrop sharp & bright, nothing summoned, "press ⌘+Space" hint
//  summon   → keystroke flashes, backdrop dims + blurs, bar springs in
//  typing   → prompt types into the bar
//  result   → outcome card drops below the bar
//  dismiss   → bar fades, backdrop returns to sharp, then next mode
type Phase = 'idle' | 'summon' | 'typing' | 'result' | 'dismiss';
const MODE_ORDER: Mode[] = ['standard', 'screenshot', 'ask'];
function TypewriterText({
  text,
  onComplete
}: {
  text: string;
  onComplete?: () => void;
}) {
  const [chars, setChars] = useState(0);
  useEffect(() => {
    setChars(0);
    const interval = setInterval(() => {
      setChars(c => {
        if (c >= text.length) {
          clearInterval(interval);
          onComplete?.();
          return c;
        }
        return c + 1;
      });
    }, 38);
    return () => clearInterval(interval);
  }, [text, onComplete]);
  return <span data-id="element-4399" data-mp="g15ghtulg9lb">{text.slice(0, chars)}</span>;
}
const PROMPTS: Record<Mode, string> = {
  standard: 'Remind me to follow up with Sarah tomorrow.',
  screenshot: 'Summarize the metrics in this dashboard.',
  ask: "What's our Q3 revenue target?"
};
function ResultText({
  mode
}: {
  mode: Mode;
}) {
  switch (mode) {
    case 'standard':
      return <>
          Reminder set — I&rsquo;ll ping you in Slack{' '}
          <strong data-id="element-4400" data-mp="3t8795chn3kj8">tomorrow at 9:00 AM</strong> to follow up with Sarah.
        </>;
    case 'screenshot':
      return <>
          Revenue is up <strong data-id="element-4401" data-mp="3oechnmvsm19l">12% MoM</strong> at $182k, pipeline sits at
          $2.4M across 42 deals, and CAC dropped to $124. Two metrics are
          trending below target — want me to dig in?
        </>;
    case 'ask':
      return <>
          Based on the latest Salesforce data, the Q3 revenue target is{' '}
          <strong data-id="element-4402" data-mp="3le2vezjt8vtz">$4.2M</strong>. You&rsquo;re currently tracking at 85% to
          goal.
        </>;
  }
}
// A faint, abstract desktop sitting behind the Spotlight overlay — overlapping
// "app windows" so it reads as your real screen, without any real branding.
function FauxDesktop() {
  return <div aria-hidden="true" className="absolute inset-0" data-id="element-4403" data-mp="340n5z89vvft">
      {/* Soft wallpaper wash */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#2C2118] via-[#241B14] to-[#1A130E]" data-id="element-4404" data-mp="32iuoi5mt07mf" />
      {/* Window A — back-left */}
      <div className="absolute top-8 left-6 md:top-12 md:left-12 w-[58%] h-[62%] rounded-xl border border-white/10 bg-[#0F0B08]/70 shadow-2xl overflow-hidden" data-id="element-4405" data-mp="3bhzicynf7cs9">
        <div className="h-7 flex items-center gap-1.5 px-3 border-b border-white/10 bg-white/[0.04]" data-id="element-4406" data-mp="3gi3ygnkf25hy">
          <span className="w-2 h-2 rounded-full bg-white/20" data-id="element-4407" data-mp="qtl9ms3mxatu" />
          <span className="w-2 h-2 rounded-full bg-white/20" data-id="element-4408" data-mp="vpirx3ygkr99" />
          <span className="w-2 h-2 rounded-full bg-white/20" data-id="element-4409" data-mp="2g8cr27q1mvmc" />
        </div>
        <div className="p-4 space-y-2.5" data-id="element-4410" data-mp="2gap3k5d18ds0">
          <div className="h-2.5 w-1/3 rounded bg-white/12" data-id="element-4411" data-mp="2b99k2hqi6fbq" />
          <div className="h-2 w-3/4 rounded bg-white/[0.07]" data-id="element-4412" data-mp="2bbvrmhde8uty" />
          <div className="h-2 w-2/3 rounded bg-white/[0.07]" data-id="element-4413" data-mp="26hi22vlexhx7" />
          <div className="h-2 w-5/6 rounded bg-white/[0.07]" data-id="element-4414" data-mp="21k9kaw46ffwh" />
          <div className="mt-4 grid grid-cols-3 gap-2.5" data-id="element-4415" data-mp="21hnbyu9gowd7">
            <div className="h-12 rounded-lg bg-white/[0.05]" data-id="element-4416" data-mp="1xj62i42kj9sm" />
            <div className="h-12 rounded-lg bg-white/[0.05]" data-id="element-4417" data-mp="1sj1pio0vcx75" />
            <div className="h-12 rounded-lg bg-white/[0.05]" data-id="element-4418" data-mp="1shqhgc0e53a8" />
          </div>
        </div>
      </div>
      {/* Window B — front-right */}
      <div className="absolute bottom-8 right-6 md:bottom-12 md:right-12 w-[52%] h-[58%] rounded-xl border border-white/10 bg-[#0F0B08]/80 shadow-2xl overflow-hidden" data-id="element-4419" data-mp="1il3xrqjswi9s">
        <div className="h-7 flex items-center gap-1.5 px-3 border-b border-white/10 bg-white/[0.04]" data-id="element-4420" data-mp="3hvvmtrkvdh90">
          <span className="w-2 h-2 rounded-full bg-white/20" data-id="element-4421" data-mp="3cwsfg0hf2yxv" />
          <span className="w-2 h-2 rounded-full bg-white/20" data-id="element-4422" data-mp="3l0w35lm4y62s" />
          <span className="w-2 h-2 rounded-full bg-white/20" data-id="element-4423" data-mp="3pye1ki2j12wj" />
        </div>
        <div className="p-4 space-y-2.5" data-id="element-4424" data-mp="3pwbfua8epvxm">
          <div className="h-2.5 w-2/5 rounded bg-white/12" data-id="element-4425" data-mp="3b26ixm6ro9jd" />
          <div className="flex gap-2 mt-1" data-id="element-4426" data-mp="3b3rhro046en1">
            <div className="h-16 flex-1 rounded-lg bg-white/[0.05]" data-id="element-4427" data-mp="3fzgj9wvp25lz" />
            <div className="h-16 flex-1 rounded-lg bg-white/[0.05]" data-id="element-4428" data-mp="329czrdo78q08" />
          </div>
          <div className="h-2 w-4/5 rounded bg-white/[0.07]" data-id="element-4429" data-mp="36zted13raho6" />
          <div className="h-2 w-3/5 rounded bg-white/[0.07]" data-id="element-4430" data-mp="371ctuphl8oow" />
        </div>
      </div>
      {/* Menu bar suggestion at top */}
      <div className="absolute top-0 inset-x-0 h-6 bg-black/30 border-b border-white/[0.06] flex items-center px-3 gap-3" data-id="element-4431" data-mp="2s92ohylq5snp">
        <span className="w-2.5 h-2.5 rounded-full bg-white/15" data-id="element-4432" data-mp="2jah4s04io7lc" />
        <span className="h-1.5 w-10 rounded bg-white/10" data-id="element-4433" data-mp="2jcbtlpiwsjup" />
        <span className="h-1.5 w-8 rounded bg-white/10" data-id="element-4434" data-mp="2nare9mripg4y" />
        <span className="ml-auto h-1.5 w-6 rounded bg-white/10" data-id="element-4435" data-mp="24nikqq7zofgn" />
      </div>
    </div>;
}
export function QuickCapture() {
  const copy = useCopy();
  const reducedMotion = useReducedMotion();
  const [modeIndex, setModeIndex] = useState(0);
  const [phase, setPhase] = useState<Phase>('idle');
  const [keyFlash, setKeyFlash] = useState(false);
  const mode = MODE_ORDER[modeIndex];
  // Drive the self-playing sequence with a chain of timers per phase.
  useEffect(() => {
    if (reducedMotion) {
      // Static: show the summoned bar + result, no looping animation.
      setPhase('result');
      return;
    }
    let timers: ReturnType<typeof setTimeout>[] = [];
    const push = (fn: () => void, ms: number) => timers.push(setTimeout(fn, ms));
    if (phase === 'idle') {
      // Hold the bright desktop, then fire the keystroke.
      push(() => setKeyFlash(true), 900);
      push(() => {
        setKeyFlash(false);
        setPhase('summon');
      }, 1180);
    } else if (phase === 'summon') {
      // Bar springs in, then begins typing.
      push(() => setPhase('typing'), 520);
    } else if (phase === 'result') {
      // Hold the answer, then dismiss.
      push(() => setPhase('dismiss'), 3400);
    } else if (phase === 'dismiss') {
      // Backdrop returns, advance to next mode and replay.
      push(() => {
        setModeIndex(i => (i + 1) % MODE_ORDER.length);
        setPhase('idle');
      }, 900);
    }
    return () => timers.forEach(clearTimeout);
  }, [phase, reducedMotion]);
  const summoned = phase === 'summon' || phase === 'typing' || phase === 'result';
  const showResult = phase === 'result' || reducedMotion;
  return <section className="relative w-full bg-[#1C1510] py-16 md:py-32 overflow-hidden" data-id="element-4436" data-mp="2qs2giuwgaukp">
      <div className="max-w-[1280px] mx-auto px-6 md:px-10 relative z-10" data-id="element-4437" data-mp="2vpcgv5ldoqnb">
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16" data-id="element-4438" data-mp="webbk8p2gyvl">
          <span className="block text-xs font-semibold tracking-[5px] uppercase text-[#EA580C] mb-5 md:mb-6" data-id="element-4439" data-mp="11cmlskj9xbec">
            Always one keystroke away
          </span>
          <h2 className="text-3xl md:text-5xl font-title font-medium text-white tracking-tight leading-[1.1] mb-6" data-id="element-4440" data-mp="tuen2unvfx6h">
            {copy.quickCapture.headline}
          </h2>
          <p className="text-base md:text-lg text-neutral-400 leading-relaxed" data-id="element-4441" data-mp="k4buuwokqn97">
            Never break your flow. Press{' '}
            <kbd className="px-2 py-1 bg-white/10 rounded-md text-sm font-mono mx-1" data-id="element-4442" data-mp="f60ncslr4ou5">
              ⌘+Space
            </kbd>{' '}
            anywhere on your computer to dictate a task, capture your screen, or
            ask a quick question.
          </p>
        </div>

        {/* Spotlight demo — Giga is summoned over a faux desktop */}
        <div className="relative w-full max-w-4xl mx-auto rounded-2xl border border-white/10 overflow-hidden shadow-2xl" data-id="element-4443" data-mp="ysgf4539z6u3">
          <div className="relative h-[420px] md:h-[440px]" data-id="element-4444" data-mp="3nl26ps6u7uit">
            {/* The desktop, behind everything */}
            <FauxDesktop data-id="element-4445" data-mp="3jhmcf2i8q69a" />

            {/* Dim + blur veil that intensifies the moment Giga is summoned */}
            <motion.div aria-hidden="true" className="absolute inset-0 bg-black/55 backdrop-blur-md" initial={false} animate={{
            opacity: summoned ? 1 : 0
          }} transition={{
            duration: 0.45,
            ease: 'easeOut'
          }} data-id="element-4446" data-mp="1mg848wwi90kp" />

            {/* Idle hint + keystroke flash (only while not summoned) */}
            <AnimatePresence data-id="element-4447" data-mp="265qvnzgn0ksf">
              {!summoned && !reducedMotion && <motion.div key="hint" initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.3
            }} className="absolute inset-0 flex items-center justify-center" data-id="element-4448" data-mp="266aerji0xf81">
                  <motion.div className="flex items-center gap-3 px-5 py-3 rounded-xl bg-black/40 backdrop-blur-sm border border-white/10" animate={{
                scale: keyFlash ? 0.94 : 1,
                borderColor: keyFlash ? 'rgba(234,88,12,0.7)' : 'rgba(255,255,255,0.1)'
              }} transition={{
                duration: 0.18
              }} data-id="element-4449" data-mp="21d7fqvuh9dkg">
                    <span className="text-sm text-neutral-300 font-medium" data-id="element-4450" data-mp="2z7ia0b7xcram">
                      Press
                    </span>
                    <kbd className={`px-2.5 py-1.5 rounded-md text-sm font-mono font-semibold transition-colors ${keyFlash ? 'bg-[#EA580C] text-white' : 'bg-white/10 text-white'}`} data-id="element-4451" data-mp="3cz6im4iysxmu">
                      ⌘ + Space
                    </kbd>
                  </motion.div>
                  {/* Expanding ring when the key fires */}
                  <AnimatePresence data-id="element-4452" data-mp="2u9gs3tm8iy0q">
                    {keyFlash && <motion.span className="absolute w-40 h-40 rounded-full border border-[#EA580C]/60" initial={{
                  scale: 0.4,
                  opacity: 0.7
                }} animate={{
                  scale: 1.6,
                  opacity: 0
                }} exit={{
                  opacity: 0
                }} transition={{
                  duration: 0.6,
                  ease: 'easeOut'
                }} data-id="element-4453" data-mp="2ua9oc0nki54m" />}
                  </AnimatePresence>
                </motion.div>}
            </AnimatePresence>

            {/* The summoned Giga panel */}
            <div className="absolute inset-0 flex items-center justify-center p-6 pointer-events-none" data-id="element-4454" data-mp="2r3104i4p1gw6">
              <AnimatePresence mode="wait" data-id="element-4455" data-mp="2m3og0557inxa">
                {summoned && <motion.div key={mode} initial={reducedMotion ? {
                opacity: 1,
                scale: 1,
                y: 0
              } : {
                opacity: 0,
                scale: 0.9,
                y: 14
              }} animate={{
                opacity: 1,
                scale: 1,
                y: 0
              }} exit={{
                opacity: 0,
                scale: 0.96,
                y: -10
              }} transition={{
                type: 'spring',
                stiffness: 420,
                damping: 30
              }} className="w-full max-w-lg flex flex-col gap-3" data-id="element-4456" data-mp="2i34ih0gq1iam">
                    {/* Screenshot context thumbnail (screenshot mode only) */}
                    {mode === 'screenshot' && <motion.div initial={{
                  opacity: 0,
                  y: 8
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  delay: 0.1
                }} className="self-start relative w-32 h-20 rounded-lg overflow-hidden border border-white/20 shadow-lg ml-1" data-id="element-4457" data-mp="dw1fuqi57liq">
                        <img src="/How_it_works_(4).png" alt="Screenshot context" className="w-full h-full object-cover opacity-80" data-id="element-4458" data-mp="3r3czo01yz9nx" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end p-2" data-id="element-4459" data-mp="3w1xz3x9edpbb">
                          <span className="text-[10px] text-white font-medium" data-id="element-4460" data-mp="4ubn9hciygjd">
                            Screen captured
                          </span>
                        </div>
                      </motion.div>}

                    {/* Command bar */}
                    <div className="relative bg-[#2B211A]/95 backdrop-blur-xl border border-white/10 shadow-2xl rounded-2xl p-4 flex items-center gap-4 ring-1 ring-[#EA580C]/20" data-id="element-4461" data-mp="qmedsv910tm0">
                      <div className="shrink-0 w-10 h-10 rounded-full bg-black/40 flex items-center justify-center relative" data-id="element-4462" data-mp="10m5an3l27kp9">
                        {phase === 'typing' && <span className="absolute inset-0 rounded-full bg-[#EA580C]/20 animate-ping" data-id="element-4463" data-mp="vnafr39ti5os" />}
                        <Mic size={20} className="text-[#EA580C] relative z-10" data-id="element-4464" data-mp="7x7up74blzzo" />
                      </div>
                      <div className="flex-1 text-base md:text-lg text-white font-medium min-h-[1.6em] flex items-center" data-id="element-4465" data-mp="2vquzexkojko">
                        {phase === 'typing' ? <>
                            <TypewriterText text={PROMPTS[mode]} onComplete={() => setPhase('result')} data-id="element-4466" data-mp="cuge1b17ga2i" />
                            <span className="inline-block w-0.5 h-5 bg-[#EA580C] ml-1 animate-pulse align-middle" data-id="element-4467" data-mp="3la01tuj3x0a6" />
                          </> : phase === 'summon' && !reducedMotion ?
                    // Bar has just sprung in — nothing typed yet. Show
                    // only a blinking caret so the prompt doesn't flash
                    // fully-typed before the typewriter begins.
                    <span className="inline-block w-0.5 h-5 bg-[#EA580C] animate-pulse align-middle" data-id="element-4468" data-mp="1ly45n056mmaw" /> :
                    // result / dismiss / reduced-motion → typing is done,
                    // show the complete prompt.
                    <span data-id="element-4469" data-mp="2ejdue43y7wox">{PROMPTS[mode]}</span>}
                      </div>
                    </div>

                    {/* Result card */}
                    <AnimatePresence data-id="element-4470" data-mp="25laafc8m37z8">
                      {showResult && <motion.div initial={reducedMotion ? {
                    opacity: 1
                  } : {
                    opacity: 0,
                    y: -8
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} exit={{
                    opacity: 0
                  }} transition={{
                    duration: 0.35
                  }} className="bg-[#2B211A]/95 backdrop-blur-xl border border-white/10 shadow-2xl rounded-2xl p-5" data-id="element-4471" data-mp="25mv12qrt26rt">
                          <div className="flex items-start gap-4" data-id="element-4472" data-mp="14oa1h2gu7jej">
                            {mode === 'standard' ? <div className="shrink-0 w-8 h-8 rounded-lg bg-green-500/15 flex items-center justify-center" data-id="element-4473" data-mp="zp748473mhh4">
                                <Check size={18} strokeWidth={3} className="text-green-400" data-id="element-4474" data-mp="1xdh70lepl99a" />
                              </div> : <div className="shrink-0 w-8 h-8 rounded-lg bg-[#EA580C] flex items-center justify-center" data-id="element-4475" data-mp="1sdcn0leldweb">
                                <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="Giga" className="w-4 h-4 object-contain [filter:brightness(0)_invert(1)]" data-id="element-4476" data-mp="1ndgkkqljmd0v" />
                              </div>}
                            <div className="flex-1 text-[15px] text-white/90 leading-relaxed pt-1" data-id="element-4477" data-mp="7x82f17a0kgk">
                              <ResultText mode={mode} data-id="element-4478" data-mp="2jmnwe7azqrcg" />
                            </div>
                          </div>
                        </motion.div>}
                    </AnimatePresence>
                  </motion.div>}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>;
}